/**
 * The SEO score, computed in the browser (SEO master prompt §14).
 *
 * A deliberate mirror of `backend/src/modules/seo/seo.rules.ts`. The score has
 * to move as an admin types — a number that only appears after saving is a
 * number nobody uses — and that means it cannot be a round trip.
 *
 * Same weights, same thresholds, same wording, so the panel and any
 * server-side audit agree. Same family as the mirrored fallback chain in
 * `product-seo.ts`; both are pinned by tests on this side, and both drift
 * silently if the server copy moves. See debt #44 in `docs/PHASES.md`.
 *
 * **This is advisory.** Nothing it says blocks a save. A merchandiser who
 * wants a 70-character title has a reason, and a tool that overrules the
 * person using it just gets worked around.
 */

export type SeoCheckLevel = 'good' | 'warning' | 'missing';

export interface SeoCheck {
  id: string;
  label: string;
  level: SeoCheckLevel;
  detail?: string;
}

export interface SeoScoreResult {
  score: number;
  checks: SeoCheck[];
}

export interface ScoreInput {
  /** What will actually render, after fallbacks — not what is in the input. */
  title: string;
  description: string;
  titleIsCustom: boolean;
  descriptionIsCustom: boolean;
  focusKeyword: string | null;
  canonicalUrl: string | null;
  ogImageUrl: string | null;
  robotsIndex: boolean;
  hasSchema: boolean;
}

const TITLE = { min: 30, ideal: 60 } as const;
const DESCRIPTION = { min: 70, ideal: 155 } as const;

/** Weights sum to 100, ordered by how much each one moves real traffic. */
const WEIGHTS = {
  title: 15,
  titleLength: 10,
  description: 15,
  descriptionLength: 10,
  focusKeyword: 10,
  keywordInTitle: 10,
  keywordInDescription: 10,
  canonical: 5,
  ogImage: 10,
  schema: 5,
} as const;

function contains(haystack: string, needle: string | null): boolean {
  return (
    needle !== null &&
    needle.trim() !== '' &&
    haystack.toLowerCase().includes(needle.toLowerCase().trim())
  );
}

export function scoreSeo(input: ScoreInput): SeoScoreResult {
  const checks: SeoCheck[] = [];
  let earned = 0;

  const award = (weight: number, check: SeoCheck): void => {
    checks.push(check);
    if (check.level === 'good') {
      earned += weight;
    } else if (check.level === 'warning') {
      earned += weight / 2;
    }
  };

  award(WEIGHTS.title, {
    id: 'title',
    label: 'Meta title',
    level: input.titleIsCustom ? 'good' : 'warning',
    detail: input.titleIsCustom ? undefined : 'Derived from the page, not written for search.',
  });

  const titleLength = input.title.length;
  award(WEIGHTS.titleLength, {
    id: 'title-length',
    label: 'Title length',
    level:
      titleLength >= TITLE.min && titleLength <= TITLE.ideal
        ? 'good'
        : titleLength === 0
          ? 'missing'
          : 'warning',
    detail:
      titleLength > TITLE.ideal
        ? `${titleLength} characters — search will truncate past ${TITLE.ideal}.`
        : titleLength < TITLE.min && titleLength > 0
          ? `${titleLength} characters — short enough to look unfinished.`
          : undefined,
  });

  award(WEIGHTS.description, {
    id: 'description',
    label: 'Meta description',
    level: input.descriptionIsCustom ? 'good' : 'warning',
    detail: input.descriptionIsCustom
      ? undefined
      : 'Derived from the page, not written for search.',
  });

  const descriptionLength = input.description.length;
  award(WEIGHTS.descriptionLength, {
    id: 'description-length',
    label: 'Description length',
    level:
      descriptionLength >= DESCRIPTION.min && descriptionLength <= DESCRIPTION.ideal
        ? 'good'
        : descriptionLength === 0
          ? 'missing'
          : 'warning',
    detail:
      descriptionLength > DESCRIPTION.ideal
        ? `${descriptionLength} characters — search will truncate past ${DESCRIPTION.ideal}.`
        : undefined,
  });

  const hasFocus = Boolean(input.focusKeyword && input.focusKeyword.trim());
  award(WEIGHTS.focusKeyword, {
    id: 'focus-keyword',
    label: 'Focus keyword',
    level: hasFocus ? 'good' : 'missing',
    detail: hasFocus ? undefined : 'Nothing to measure the copy against.',
  });

  award(WEIGHTS.keywordInTitle, {
    id: 'keyword-in-title',
    label: 'Focus keyword in title',
    level: !hasFocus ? 'missing' : contains(input.title, input.focusKeyword) ? 'good' : 'warning',
  });

  award(WEIGHTS.keywordInDescription, {
    id: 'keyword-in-description',
    label: 'Focus keyword in description',
    level: !hasFocus
      ? 'missing'
      : contains(input.description, input.focusKeyword)
        ? 'good'
        : 'warning',
  });

  award(WEIGHTS.canonical, {
    id: 'canonical',
    label: 'Canonical URL',
    // The resolver always emits one; an override is only needed when the page
    // duplicates another, so "not set" is the healthy default.
    level: 'good',
    detail: input.canonicalUrl ? 'Overridden.' : undefined,
  });

  award(WEIGHTS.ogImage, {
    id: 'og-image',
    label: 'Social image',
    level: input.ogImageUrl ? 'good' : 'warning',
    detail: input.ogImageUrl ? undefined : 'Falls back to the site default card.',
  });

  award(WEIGHTS.schema, {
    id: 'schema',
    label: 'Structured data',
    level: input.hasSchema ? 'good' : 'warning',
  });

  if (!input.robotsIndex) {
    checks.push({
      id: 'noindex',
      label: 'Excluded from search',
      level: 'missing',
      detail: 'This page is set to noindex, so none of the above reaches search.',
    });
  }

  return { score: Math.round(earned), checks };
}
